import { buildPageProductDetails } from '@app/analytics/track';
import { CartItemsTrayWithSimilarDesigns } from '@app/components/CartItemsTray';
import { PreconnectLinks } from '@app/components/ResourceHints';
import {
  CartAndDeliveryDatesDataProvider,
  CartCheckoutClient,
} from '@app/data/clients/cartCheckoutClient';
import { SignUpGuardProvider } from '@app/hooks/SignUpGuardContext';
import { useEngageTextMessaging } from '@app/hooks/useEngageTextMessaging';
import { usePrefersReducedMotion } from '@app/hooks/usePrefersReducedMotion';
import { Box, constants, Divider, PageSection } from '@redbubble/design-system';
import { RedbubbleLayout } from '@redbubble/next/app';
import { useViewportWidth } from '@redbubble/next/hooks';
import {
  AppProps,
  DeviceType,
  RBLayoutProps,
  WithLayout,
} from '@redbubble/next/types';
import { usePageProps } from '@redbubble/next-provider';
import {
  RSPageType,
  useRSPageView,
} from '@redbubble/next-provider/rudderstack';
import { NextPage } from 'next';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import Confetti from 'react-dom-confetti';
import { ComplianceLinks } from './components/ComplianceLinks';
import OffsiteSimilarDesignsContainer from './components/OffsiteSimilarDesignsContainer/OffsiteSimilarDesignsContainer';
import { PrimaryContentSection } from './components/PrimaryContentSection/PrimaryContentSection';
import { SEOTags } from './components/SEOTags';
import { SecondaryContentSection } from './components/SecondaryContentSection';
import { DD_PDP_ERROR_METRIC_NAME } from './constants';
import { CurrentInventoryStoreProvider } from './hooks/useCurrentInventoryItem';
import styles from './index.module.scss';
import { ProductPageProps } from './types';
import { SimilarDesignsProvider } from './utils/SimilarDesignsContext';

const SignUpGuard = dynamic(
  () =>
    import(
      /* webpackChunkName: "SignUpGuard" */
      '@app/components/SignUpGuard/SignUpGuard'
    ).then(({ SignUpGuard: Component }) => Component),
  { ssr: false, loading: () => null }
);

const ReactToastContainer = dynamic(
  () =>
    import(
      /* webpackChunkName: "ReactToastContainer" */
      '@app/components/Toasts/ReactToastContainer'
    ).then(({ ReactToastContainer: Component }) => Component),
  { ssr: false, loading: () => null }
);

const cartCheckoutClient = new CartCheckoutClient({
  logging: {
    metricName: DD_PDP_ERROR_METRIC_NAME,
    context: 'ProductPage',
  },
});

const ProductPage: WithLayout<NextPage<ProductPageProps>> = ({
  inventoryCategorySet,
  defaultInventoryItems,
  reviewSummary,
  alternateProductUrls,
  isOffsiteVisitor,
  groupId,
  initialInventoryItem,
}) => {
  const { productTypes, productFamilies } = usePageProps<ProductPageProps>();

  // work lives on defaultItem only — InventoryItem (items array) lacks the work GQL fragment
  const work = inventoryCategorySet.defaultItem.work;
  useRSPageView(
    RSPageType.Product,
    // fires once per navigation, not per variant change (variant tracking stays in useAnalytics)
    work ? buildPageProductDetails(initialInventoryItem, work) : undefined
  );
  const { deviceType, preconnectLinks, isBot } = usePageProps<
    AppProps & ProductPageProps
  >();
  const { isOrAbove } = useViewportWidth(deviceType);

  const [atcDrawerOpen, setAtcDrawerOpen] = useState<boolean>(false);

  const [confettiActive, setConfettiActive] = useState<boolean>(false);

  const [reducedMotion, setReducedMotion] = useState<boolean>(false);

  const prefersReducedMotion = usePrefersReducedMotion();

  const isMobile = !isOrAbove(DeviceType.Tablet);

  useEffect(() => {
    setReducedMotion(prefersReducedMotion);
  }, [prefersReducedMotion]);

  return (
    <CurrentInventoryStoreProvider
      initialItem={initialInventoryItem}
      inventoryCategorySet={inventoryCategorySet}
    >
      <PreconnectLinks preconnectLinks={preconnectLinks} />
      {!reducedMotion && (
        <Box className={styles.confetti} data-testid="confetti-box">
          <Confetti
            active={confettiActive}
            config={{
              colors: ['#D92D02', '#FA8F0F', '#ADECF0', '#51BA88'],
              angle: 270,
              spread: isMobile ? 129 : 290,
              startVelocity: isMobile ? 30 : 70,
              elementCount: isMobile ? 150 : 300,
              duration: isMobile ? 2800 : 1400,
            }}
          />
        </Box>
      )}
      <SignUpGuardProvider>
        <SignUpGuard />
        {/* TODO: SEO-46 created a tool to use that appends x tags onto the title tag */}
        <SEOTags
          availableProducts={defaultInventoryItems}
          reviewSummary={reviewSummary}
          alternateProductUrls={alternateProductUrls}
        />
        <CartAndDeliveryDatesDataProvider client={cartCheckoutClient}>
          <SimilarDesignsProvider
            isBot={isBot}
            groupId={groupId}
            isOffsiteVisitor={isOffsiteVisitor}
          >
            <CartItemsTrayWithSimilarDesigns
              open={atcDrawerOpen}
              onCloseRequested={() => {
                setAtcDrawerOpen(false);
                setConfettiActive(false);
              }}
              rsPageType={RSPageType.Product}
            />

            <Box element="main">
              <PageSection size={constants.MEDIUM}>
                <OffsiteSimilarDesignsContainer />
                <PrimaryContentSection
                  setAtcDrawerOpen={setAtcDrawerOpen}
                  setConfettiActive={setConfettiActive}
                  productTypes={productTypes}
                  productFamilies={productFamilies}
                />
                <Divider marginY="xxl" />
                <SecondaryContentSection />
              </PageSection>
            </Box>
          </SimilarDesignsProvider>
        </CartAndDeliveryDatesDataProvider>
      </SignUpGuardProvider>
      <ReactToastContainer />
      <ComplianceLinks />
    </CurrentInventoryStoreProvider>
  );
};

const ProductPageLayout = (props: RBLayoutProps) => {
  const engagingPlaceholderMessages = useEngageTextMessaging();

  return RedbubbleLayout({ ...props, engagingPlaceholderMessages });
};

ProductPage.layout = ProductPageLayout;

export default ProductPage;
